import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSOWDataStore, useTranslation } from '../../store';
import '../../css/Components.css';
import Loading from '../Loading';
import LoadingError from '../LoadingError';
import NoData from '../NoData';
import { queryParamsService } from '../../services/queryParams';
import { useSignatureStore } from '../../store/useSignatureStore';
import { CONSTANTS } from '../../constants/constants';
import { isSignatureRequired, isValueEmpty } from '../../services/utils';

const ComponentsTab = () => {
  const { sowData, sowLoading, sowError, fetchSOWData } = useSOWDataStore();
  const {
    jobsSignatures,
    loading: signatureLoading,
    error: signatureError,
    submitStatus,
    isSubmitting,
    hasHydrated,
    setSignature,
    clearSignature,
    setSubmitStatus,
    fetchSignatureData,
    submitSignature,
    initializeContactPersonFromSOW,
    fetchLocation,
    setContactPerson,
    setComments,
    setTimestamp,
    setIsSubmitting
  } = useSignatureStore();
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const isDrawingRef = useRef(false);
  const lastPositionRef = useRef({ x: 0, y: 0 });
  const [pickupDateTime, setPickupDateTime] = useState('');
  const canvasRef = useRef(null);
  const { t } = useTranslation();
  const requiresSignature =
    isSignatureRequired(sowData?.scopeOfWork?.requiresPickupSignature);
  const partsReturnRequired = isSignatureRequired(sowData?.scopeOfWork?.partsReturnRequired);

  const getCurrentSignatureData = () => {
    const sowKey = queryParamsService.getIssueKey();
    return jobsSignatures[sowKey] || {
      contactPerson: '',
      signature: null,
      timestamp: null,
      location: null,
      comments: '',
      status: CONSTANTS.STATUS.PENDING
    };
  };

  const signatureData = useMemo(() => getCurrentSignatureData(), [jobsSignatures]);
  const hasCapturedSignature = Boolean(signatureData.signature);
  const showSignatureSection = requiresSignature || hasCapturedSignature;

  useEffect(() => {
    const fetchData = async () => {
      queryParamsService.initialize();
      await fetchSOWData();
    };
    fetchData();
  }, [fetchSOWData]);

  useEffect(() => {
    if (!hasHydrated || !showSignatureSection) {
      return;
    }

    setSubmitStatus(null);
    fetchSignatureData(true);
  }, [hasHydrated, showSignatureSection, setSubmitStatus, fetchSignatureData]);

  useEffect(() => {
    if (showSignatureSection && sowData && !signatureData.contactPerson) {
      initializeContactPersonFromSOW(sowData);
    }
  }, [showSignatureSection, sowData, signatureData.contactPerson]);

  useEffect(() => {
    if (!showSignatureSection) {
      return;
    }

    // Keep input value stable: use saved timestamp when present, otherwise prefill with current local time.
    const nextValue = !isValueEmpty(signatureData.timestamp)
      ? formatDateTimeLocal(signatureData.timestamp)
      : (pickupDateTime || formatDateTimeLocal());

    if (nextValue && pickupDateTime !== nextValue) {
      setPickupDateTime(nextValue);
    }
  }, [showSignatureSection, signatureData.timestamp]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || isSignatureInactive()) {
      return;
    }

    const preventTouchScroll = (event) => {
      event.preventDefault();
    };

    canvas.addEventListener('touchmove', preventTouchScroll, { passive: false });
    return () => {
      canvas.removeEventListener('touchmove', preventTouchScroll);
    };
  }, [showSignatureSection, signatureData.status]);

  const formatDateTimeLocal = (value) => {
    const dateValue = value ? new Date(value) : new Date();
    if (Number.isNaN(dateValue.getTime())) {
      return '';
    }
    const pad = (part) => String(part).padStart(2, '0');
    const formattedDate = [
      dateValue.getFullYear(),
      pad(dateValue.getMonth() + 1),
      pad(dateValue.getDate())
    ].join('-');
    const formattedTime = [pad(dateValue.getHours()), pad(dateValue.getMinutes())].join(':');
    return `${formattedDate}T${formattedTime}`;
  };

  useEffect(() => {
    if (!showSignatureSection || isSignatureInactive()) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#000000';
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (signatureData.signature) {
      const image = new Image();
      image.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      };
      image.src = signatureData.signature;
    }
  }, [showSignatureSection, signatureData.signature, signatureData.status]);

  // Get available sort fields from the first component (if available)
  const availableSortFields = useMemo(() => {
    if (!sowData?.scopeOfWork?.components?.length) return [];

    const component = sowData.scopeOfWork.components[0];
    return Object.keys(component)
      .filter((key) =>
        typeof component[key] === 'string' ||
        typeof component[key] === 'number' ||
        typeof component[key] === 'boolean'
      )
      .map((key) => ({
        value: key,
        label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
      }));
  }, [sowData]);

  // Filter and sort components
  const filteredAndSortedComponents = useMemo(() => {
    if (!sowData?.scopeOfWork?.components) return [];

    let components = [...sowData.scopeOfWork.components];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      components = components.filter((component) =>
        Object.values(component).some(
          (value) => value && value.toString().toLowerCase().includes(term)
        )
      );
    }

    // Apply sorting
    components.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (aValue === undefined || aValue === null) aValue = '';
      if (bValue === undefined || bValue === null) bValue = '';

      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return components;
  }, [sowData, sortField, sortDirection, searchTerm]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const isSignatureInactive = () => signatureData.status === CONSTANTS.STATUS.COMPLETED;

  const getCanvasCoordinates = (eventPoint, canvas) => {
    const rect = canvas.getBoundingClientRect();
    // Convert CSS-pixel pointer positions to canvas-pixel coordinates.
    const scaleX = rect.width ? canvas.width / rect.width : 1;
    const scaleY = rect.height ? canvas.height / rect.height : 1;

    return {
      x: (eventPoint.clientX - rect.left) * scaleX,
      y: (eventPoint.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (event) => {
    if (isSignatureInactive()) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const { x, y } = getCanvasCoordinates(event, canvas);

    isDrawingRef.current = true;
    lastPositionRef.current = { x, y };
  };

  const draw = (event) => {
    if (!isDrawingRef.current || isSignatureInactive()) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(event, canvas);

    ctx.beginPath();
    ctx.moveTo(lastPositionRef.current.x, lastPositionRef.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();

    lastPositionRef.current = { x, y };
  };

  const stopDrawing = () => {
    if (isSignatureInactive()) {
      return;
    }

    isDrawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const hasSignature = imageData.data.some((channel) => channel !== 0);
    if (hasSignature) {
      if (!signatureData.timestamp) {
        setTimestamp(new Date().toISOString());
      }
      setSignature(canvas.toDataURL());
    }
  };

  const clearCanvas = () => {
    if (isSignatureInactive()) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#000000';
    isDrawingRef.current = false;
    lastPositionRef.current = { x: 0, y: 0 };
    clearSignature();
  };

  const handlePickupDateChange = (event) => {
    const value = event.target.value;
    setPickupDateTime(value);
    if (!value) {
      return;
    }
    setTimestamp(new Date(value).toISOString());
  };

  const handleSignatureSubmit = async () => {
    if (!signatureData.signature) {
      setSubmitStatus({ type: 'error', message: t('pleaseProvideSignature') });
      return;
    }

    if (!signatureData.contactPerson.trim()) {
      setSubmitStatus({ type: 'error', message: t('pleaseEnterContactPerson') });
      return;
    }

    try {
      setIsSubmitting(true);
      setComments('Technician picked up the components.');
      const timestampToUse = pickupDateTime ? new Date(pickupDateTime).toISOString() : new Date().toISOString();
      setTimestamp(timestampToUse);
      // Location is optional for this flow; keep submit unblocked if GPS is denied/unavailable.
      try {
        await fetchLocation(t);
      } catch (locationError) {
        setSubmitStatus({
          type: 'warning',
          message: locationError?.message || t('locationInfoUnavailable')
        });
      }

      const submitResponse = await submitSignature(t);
      if (submitResponse) {
        await fetchSignatureData(true);
      }
    } catch (error) {
      setIsSubmitting(false);
      setSubmitStatus({
        type: 'error',
        message: error?.message || t('errorLoadingSignature', { error: '' })
      });
    }
  };

  if (sowLoading) {
    return (
        <Loading message={t('loadingComponents')}/>
    );
  }

  if (sowError) {
    return (
        <LoadingError message={t('errorLoadingComponents', {'error': sowError})}/>
    );
  }
  
  if (!sowData) {
    return (
      <NoData/>
    );
  }

  return (
    <div className="tab-content com components-tab">
      <div className="components-header">
        <h2>{t('components')}</h2>
        <p>{t('workOrder')} {sowData.workorderKey}</p>
        {(requiresSignature && !hasCapturedSignature) || partsReturnRequired ? (
          <div className="required-tags-container">
            {requiresSignature && !hasCapturedSignature && (
              <span className="signature-required-tag">{t('signatureRequired')}</span>
            )}
            {partsReturnRequired && (
              <span className="parts-return-required-tag">{t('partsReturnRequired')}</span>
            )}
          </div>
        ) : null}

        <div className="controls-row">
          <div className="search-box">
            <input
              type="text"
              placeholder={t('searchComponents')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="sort-controls">
            <label htmlFor="sort-field">{t('sortBy')}</label>
            <select
              id="sort-field"
              value={sortField}
              onChange={(e) => handleSort(e.target.value)}
              className="sort-select"
            >
              {availableSortFields.map(field => (
                <option key={field.value} value={field.value}>
                  {t(field.label)}
                </option>
              ))}
            </select>
            
            <button
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
              className="sort-direction-btn"
            >
              {sortDirection === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
        
        <div className="summary-info">
          <span>{t('totalComponents')} {filteredAndSortedComponents.length}</span>
          {searchTerm && (
            <span className="filtered-count">
              ({t('filteredFrom',{'from': sowData.scopeOfWork.components.length})})
            </span>
          )}
        </div>
      </div>

      <div className="components-grid">
        <div className="components-list">
          {filteredAndSortedComponents.map((component, index) => (
            <div key={index} className="component-card">
              <div className="component-header">
                <h3>{component.name || 'Unnamed Component'}</h3>
                <span className={`status-badge status-${component.status?.toLowerCase() || 'unknown'}`}>
                  {component.status || 'Unknown Status'}
                </span>
              </div>
              
              <div className="component-details-grid">
                <div className="detail-item">
                  <label>{t('code')}</label>
                  <span>{component.code || t('na')}</span>
                </div>
                
                <div className="detail-item">
                  <label>{t('partNumber')}</label>
                  <span>{component.partNumber || t('na')}</span>
                </div>
                
                <div className="detail-item">
                  <label>{t('quantity')}</label>
                  <span>{component.quantity ?? t('na')}</span>
                </div>
                
                <div className="detail-item">
                  <label>{t('organization')}</label>
                  <span>{component.organization || t('na')}</span>
                </div>
                
                <div className="detail-item">
                  <label>{t('warehouse')}</label>
                  <span>{component.warehouseCode || t('na')}</span>
                </div>
                
                <div className="detail-item">
                  <label>{t('binCode')}</label>
                  <span>{component.binCode || t('na')}</span>
                </div>
                
                <div className="detail-item">
                  <label>{t('active')}</label>
                  <span className={component.active ? 'active-true' : 'active-false'}>
                    {component.active ? t('yes') : t('no')}
                  </span>
                </div>
                
                <div className="detail-item">
                  <label>{t('serialized')}</label>
                  <span>{component.isSerialized ? t('yes') : t('no')}</span>
                </div>
                
                <div className="detail-item">
                  <label>{t('orderable')}</label>
                  <span>{component.orderable ? t('yes') : t('no')}</span>
                </div>
                
                <div className="detail-item">
                  <label>{t('consumable')}</label>
                  <span>{component.consumable ? t('yes') : t('no')}</span>
                </div>
              </div>
              
              {component.vivifyInternalPartNumber && (
                <div className="internal-part">
                  <label>{t('internalPartN')}</label>
                  <span>{component.vivifyInternalPartNumber}</span>
                </div>
              )}
              {component.serial && (
                <div className="internal-part-item">
                  <label>{t('serialN')}</label>
                  <span>{component.serial}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {filteredAndSortedComponents.length === 0 && searchTerm && (
        <div className="no-results">
          {t('noComponentsFound', {'search': searchTerm})}
        </div>
      )}

      {showSignatureSection && (
        <div className="components-signature-section">
          <h3>{t('technicianSignature')}</h3>
          <p className="components-signature-helper">{t('technicianSignatureHelper')}</p>

          {signatureLoading && (
            <div className="components-signature-info">{t('loadingSignature')}</div>
          )}

          {signatureError && (
            <div className="submit-status error">{t('errorLoadingSignature', { error: signatureError })}</div>
          )}

          <div className="components-signature-form">
            <div className="form-group">
              <label htmlFor="component-technician-name">{t('technicianNameLabel')}</label>
              <input
                id="component-technician-name"
                type="text"
                value={signatureData.contactPerson}
                onChange={(event) => setContactPerson(event.target.value)}
                placeholder={t('enterNamePlaceholder')}
                className="form-input"
                disabled={isSignatureInactive()}
              />
            </div>

            <div className="form-group">
              <label htmlFor="pickup-date-time">{t('pickupDateTimeLabel')}</label>
              <input
                id="pickup-date-time"
                type="datetime-local"
                value={pickupDateTime}
                onChange={handlePickupDateChange}
                className="form-input"
                disabled={isSignatureInactive()}
              />
            </div>

            <div className="form-group">
              <label>{t('signature')} *</label>
              <div className="signature-canvas-container components-canvas-wrap">
                {isSignatureInactive() && signatureData.signature ? (
                  <img
                    src={signatureData.signature}
                    alt={t('signature')}
                    className="signature-canvas signature-canvas-static"
                  />
                ) : (
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={200}
                    className="signature-canvas"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={(event) => {
                      event.preventDefault();
                      startDrawing(event.touches[0]);
                    }}
                    onTouchMove={(event) => {
                      draw(event.touches[0]);
                    }}
                    onTouchEnd={stopDrawing}
                  />
                )}
                {!isSignatureInactive() && (
                  <button
                    onClick={clearCanvas}
                    className="clear-signature-btn"
                    type="button"
                  >
                    {t('clearSignature')}
                  </button>
                )}
              </div>
            </div>

            {submitStatus && (
              <div className={`submit-status ${submitStatus.type}`}>
                {submitStatus.message}
              </div>
            )}

            {!isSignatureInactive() && (
              <button
                onClick={handleSignatureSubmit}
                disabled={isSubmitting || !signatureData.signature || !signatureData.contactPerson}
                className="submit-btn components-signature-submit"
                type="button"
              >
                {isSubmitting ? t('submitting') : t('submit')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComponentsTab;